import type { InvoiceAdditionalFilters } from '../types/invoice';

/**
 * Invoicing list view-state contract. Lives in `src/shared` because the sidebar,
 * TopBar, and both invoicing pages depend on it — shared chrome must not reach
 * into a page directory (see `.cursor/rules/yaxle-structure.mdc`).
 */

export const INVOICES_VIEW_STATE_CACHE_KEY = 'invoices_view_state_cache';
export const INVOICES_LIST_TAB_STORAGE_KEY = 'invoices_list_tab';
export const INVOICES_LIST_PAGE_STORAGE_KEY = 'invoices_list_page';
export const INVOICES_ADVANCED_FILTERS_STORAGE_KEY = 'invoice_advanced_filters';
export const INVOICES_LOCATION_IDS_STORAGE_KEY = 'location_ids';

export type InvoicesListType = 'all' | 'flagged' | 'submitted';

/** Rows the invoicing table shows per page. */
export const INVOICES_LIST_PAGE_SIZE = 20;
/**
 * The list requests one extra row to detect another page. That request size
 * is also the feed offset stride, so detail previous/next must use it too
 * or it will walk a different sequence than the table.
 */
export const INVOICES_LIST_FETCH_LIMIT = INVOICES_LIST_PAGE_SIZE + 1;

export function visibleInvoicesOnListPage<T>(fetched: readonly T[]): T[] {
    return fetched.slice(0, INVOICES_LIST_PAGE_SIZE);
}

export function invoicesListPageHasNext(fetchedLength: number): boolean {
    return fetchedLength > INVOICES_LIST_PAGE_SIZE;
}

export type InvoicesSidebarTab =
    | 'invoices'
    | 'invoices-flagged'
    | 'invoices-submitted'
    | 'invoices-archived';

export interface InvoicesViewStateCache {
    type: InvoicesListType;
    showArchived: boolean;
    showNegotiation?: boolean;
    selectedNegotiationId?: string | null;
    currentPage: number;
    searchQuery: string;
    debouncedSearch: string;
    additionalFilters: InvoiceAdditionalFilters;
    locationIds: Array<string> | 'all';
}

// The RO search is deliberately not a URL param and is never restored from
// storage: a refresh, sidebar click, or browser back lands on an empty search.
// It only survives the breadcrumb trip back from invoice detail, carried in
// `location.state.invoicesViewState`.
const LIST_SEARCH_KEYS = ['type', 'archived', 'page', 'view', 'negotiationId'] as const;

// Touching localStorage throws a SecurityError when a browser blocks site data
// (Safari "Block all cookies", Chrome site-data blocks, some embedded contexts).
// Route every read through here so a blocked store degrades to defaults instead
// of throwing during the invoicing list's first render.
function readLocalStorage(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

export function parseInvoicesListType(value: string | null | undefined): InvoicesListType | null {
    if (value === 'all' || value === 'flagged' || value === 'submitted') return value;
    return null;
}

export function parseInvoicesListPage(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return null;
}

const INVOICE_FEED_TYPES = ['received', 'returned', 'return_slip', 'sublet'] as const;
type InvoiceFeedType = (typeof INVOICE_FEED_TYPES)[number];

function readInvoiceTypes(parsed: InvoiceAdditionalFilters): InvoiceFeedType[] {
    const fromList = Array.isArray(parsed.invoiceTypes) ? parsed.invoiceTypes : [];
    const raw = fromList.length > 0
        ? fromList
        : parsed.invoiceType
            ? [parsed.invoiceType]
            : [];
    const types: InvoiceFeedType[] = [];
    const seen = new Set<string>();
    for (const entry of raw) {
        if (typeof entry !== 'string' || !INVOICE_FEED_TYPES.includes(entry as InvoiceFeedType) || seen.has(entry)) {
            continue;
        }
        seen.add(entry);
        types.push(entry as InvoiceFeedType);
    }
    return types;
}

export function parseInvoiceAdditionalFilters(value: unknown): InvoiceAdditionalFilters {
    if (!value || typeof value !== 'object') return {};
    const parsed = { ...(value as InvoiceAdditionalFilters) };
    if (parsed.uploadDateRange) {
        parsed.uploadDateRange = {
            start: new Date(parsed.uploadDateRange.start),
            end: new Date(parsed.uploadDateRange.end),
        };
    }
    const invoiceTypes = readInvoiceTypes(parsed);
    if (invoiceTypes.length > 0) parsed.invoiceTypes = invoiceTypes;
    else delete parsed.invoiceTypes;
    delete parsed.invoiceType;
    if (!Array.isArray(parsed.vendorNames)) {
        delete parsed.vendorNames;
    }
    if (!Array.isArray(parsed.flaggedReasons)) {
        delete parsed.flaggedReasons;
    }
    if (parsed.flaggedReasonsMatch !== 'exact') {
        delete parsed.flaggedReasonsMatch;
    }
    const efficiencyFlags: Array<'theirFault' | 'neededFlags' | 'match'> = [];
    if (Array.isArray(parsed.efficiencyFlags)) {
        for (const flag of parsed.efficiencyFlags) {
            if ((flag === 'theirFault' || flag === 'neededFlags' || flag === 'match') && !efficiencyFlags.includes(flag)) {
                efficiencyFlags.push(flag);
            }
        }
    }
    if (efficiencyFlags.length > 0) parsed.efficiencyFlags = efficiencyFlags;
    else delete parsed.efficiencyFlags;
    return parsed;
}

export function readStoredInvoicesLocationIds(): Array<string> | 'all' {
    try {
        const stored = readLocalStorage(INVOICES_LOCATION_IDS_STORAGE_KEY);
        if (!stored) return 'all';
        if (stored === 'all') return 'all';
        const parsed = JSON.parse(stored) as unknown;
        if (parsed === 'all') return 'all';
        if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
        }
    } catch {
        // ignore
    }
    return 'all';
}

function normalizeLocationIds(value: unknown): Array<string> | 'all' {
    if (value === 'all') return 'all';
    if (Array.isArray(value)) {
        const ids = value.filter((id): id is string => typeof id === 'string' && id.length > 0);
        return ids.length > 0 ? ids : 'all';
    }
    return 'all';
}

export function normalizeInvoicesViewState(raw: unknown): InvoicesViewStateCache | null {
    if (!raw || typeof raw !== 'object') return null;
    const value = raw as Partial<InvoicesViewStateCache>;
    const searchQuery = typeof value.searchQuery === 'string' ? value.searchQuery : '';
    return {
        type: parseInvoicesListType(value.type) ?? 'all',
        showArchived: value.showArchived === true,
        showNegotiation: value.showNegotiation === true,
        selectedNegotiationId:
            typeof value.selectedNegotiationId === 'string' && value.selectedNegotiationId.length > 0
                ? value.selectedNegotiationId
                : null,
        currentPage: typeof value.currentPage === 'number' && value.currentPage > 0 ? value.currentPage : 1,
        searchQuery,
        debouncedSearch: typeof value.debouncedSearch === 'string' ? value.debouncedSearch : searchQuery,
        additionalFilters: parseInvoiceAdditionalFilters(value.additionalFilters),
        locationIds: normalizeLocationIds(value.locationIds),
    };
}

export function readInvoicesViewStateFromLocation(state: unknown): InvoicesViewStateCache | null {
    if (!state || typeof state !== 'object') return null;
    return normalizeInvoicesViewState((state as { invoicesViewState?: unknown }).invoicesViewState);
}

export function loadInvoicesViewStateFromLocalStorage(): InvoicesViewStateCache {
    let additionalFilters: InvoiceAdditionalFilters = {};
    try {
        const saved = readLocalStorage(INVOICES_ADVANCED_FILTERS_STORAGE_KEY);
        if (saved) {
            additionalFilters = parseInvoiceAdditionalFilters(JSON.parse(saved));
        }
    } catch {
        additionalFilters = {};
    }

    return {
        type: parseInvoicesListType(readLocalStorage(INVOICES_LIST_TAB_STORAGE_KEY)) ?? 'all',
        showArchived: false,
        showNegotiation: false,
        selectedNegotiationId: null,
        currentPage: parseInvoicesListPage(readLocalStorage(INVOICES_LIST_PAGE_STORAGE_KEY)) ?? 1,
        searchQuery: '',
        debouncedSearch: '',
        additionalFilters,
        locationIds: readStoredInvoicesLocationIds(),
    };
}

/** Same view state with the RO search cleared, for restores that are not a breadcrumb return. */
export function withoutInvoicesSearch<T extends Partial<InvoicesViewStateCache> | null | undefined>(state: T): T {
    if (!state) return state;
    return { ...state, searchQuery: '', debouncedSearch: '' };
}

export function loadInvoicesViewStateCache(): InvoicesViewStateCache | null {
    if (typeof window === 'undefined') return null;
    try {
        const saved = sessionStorage.getItem(INVOICES_VIEW_STATE_CACHE_KEY);
        if (!saved) return null;
        return normalizeInvoicesViewState(JSON.parse(saved));
    } catch {
        return null;
    }
}

export function saveInvoicesViewStateCache(state: InvoicesViewStateCache): void {
    if (typeof window === 'undefined') return;
    try {
        sessionStorage.setItem(INVOICES_VIEW_STATE_CACHE_KEY, JSON.stringify(state));
        localStorage.setItem(INVOICES_LIST_TAB_STORAGE_KEY, state.type);
        localStorage.setItem(INVOICES_LIST_PAGE_STORAGE_KEY, String(state.currentPage));
        localStorage.setItem(INVOICES_ADVANCED_FILTERS_STORAGE_KEY, JSON.stringify(state.additionalFilters));
    } catch {
        // ignore
    }
}

export function hasInvoicesListSearchParams(searchParams: URLSearchParams): boolean {
    return LIST_SEARCH_KEYS.some((key) => searchParams.has(key));
}

export function buildInvoicesListSearch(state: {
    type: InvoicesListType;
    showArchived: boolean;
    showNegotiation?: boolean;
    selectedNegotiationId?: string | null;
    currentPage?: number;
}): string {
    const params = new URLSearchParams();
    if (state.showNegotiation) {
        params.set('view', 'negotiation');
        if (state.selectedNegotiationId) params.set('negotiationId', state.selectedNegotiationId);
        return params.toString();
    }
    if (state.type !== 'all') params.set('type', state.type);
    if (state.showArchived) params.set('archived', 'true');
    if (state.currentPage && state.currentPage > 1) params.set('page', String(state.currentPage));
    return params.toString();
}

export function invoicesListHref(state?: Partial<InvoicesViewStateCache> | null): string {
    if (!state) return '/invoices';
    const search = buildInvoicesListSearch({
        type: state.type ?? 'all',
        showArchived: state.showArchived ?? false,
        showNegotiation: state.showNegotiation ?? false,
        selectedNegotiationId: state.selectedNegotiationId ?? null,
        currentPage: state.currentPage ?? 1,
    });
    return search ? `/invoices?${search}` : '/invoices';
}

export function invoicesListSearchEquals(a: string, b: string): boolean {
    const left = new URLSearchParams(a.startsWith('?') ? a.slice(1) : a);
    const right = new URLSearchParams(b.startsWith('?') ? b.slice(1) : b);
    return LIST_SEARCH_KEYS.every((key) => (left.get(key) ?? '') === (right.get(key) ?? ''));
}

export function invoicesSidebarTabFromViewState(state: {
    type?: InvoicesListType;
    showArchived?: boolean;
}): InvoicesSidebarTab {
    if (state.showArchived) return 'invoices-archived';
    if (state.type === 'flagged') return 'invoices-flagged';
    if (state.type === 'submitted') return 'invoices-submitted';
    return 'invoices';
}

export function resolveInitialInvoicesViewState(args: {
    locationState: unknown;
    searchParams: URLSearchParams;
    /**
     * Search remembered for this history entry within the current page load
     * (see `listSearchMemory`). Lets browser back/forward keep the RO search
     * while a refresh, which drops that memory, still starts clean.
     */
    rememberedSearch?: string;
}): InvoicesViewStateCache {
    const fromStorage = loadInvoicesViewStateFromLocalStorage();
    // The RO search comes back only from a breadcrumb return (location state)
    // or from this page load's memory of the history entry, never from storage.
    const fromCache = withoutInvoicesSearch(loadInvoicesViewStateCache());
    const fromLocation = readInvoicesViewStateFromLocation(args.locationState);
    const remembered = args.rememberedSearch ?? '';

    if (fromLocation) {
        return {
            ...fromStorage,
            ...fromCache,
            ...fromLocation,
            additionalFilters:
                fromLocation.additionalFilters
                ?? fromCache?.additionalFilters
                ?? fromStorage.additionalFilters,
            locationIds: fromLocation.locationIds ?? fromCache?.locationIds ?? fromStorage.locationIds,
        };
    }

    if (hasInvoicesListSearchParams(args.searchParams)) {
        // A URL carrying any list param is a complete deep link: an omitted `type`
        // means "all", never the last cached tab. Otherwise `/invoices?archived=true`
        // from the sidebar would inherit Flagged/Submitted and hide most archived rows.
        return {
            ...parseInvoicesListSearchParams(args.searchParams),
            searchQuery: remembered,
            debouncedSearch: remembered,
            additionalFilters: fromCache?.additionalFilters ?? fromStorage.additionalFilters,
            locationIds: fromCache?.locationIds ?? fromStorage.locationIds,
        };
    }

    return {
        ...fromStorage,
        ...fromCache,
        searchQuery: remembered,
        debouncedSearch: remembered,
    };
}

export function parseInvoicesListSearchParams(searchParams: URLSearchParams): Pick<
    InvoicesViewStateCache,
    'type' | 'showArchived' | 'showNegotiation' | 'selectedNegotiationId' | 'currentPage'
> {
    const showNegotiation = searchParams.get('view') === 'negotiation';
    const negotiationId = searchParams.get('negotiationId');
    return {
        type: parseInvoicesListType(searchParams.get('type')) ?? 'all',
        showArchived: !showNegotiation && searchParams.get('archived') === 'true',
        showNegotiation,
        selectedNegotiationId:
            showNegotiation && negotiationId && negotiationId.length > 0 ? negotiationId : null,
        currentPage: parseInvoicesListPage(searchParams.get('page')) ?? 1,
    };
}
